import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import ExportToolbar from "../../components/ExportToolbar";
import { useLocationContext } from "context/LocationContext";
import useAuth from "hooks/useAuth";
import DpatMetricCard from "../dpat-dashboard/components/DpatMetricCard";
import PetitionTable from "./components/PetitionTable";
import PetitionReviewModal from "./components/PetitionReviewModal";
import {
  flattenDistrictsWithRegionsForPetitions,
  fetchDistrictPetitions,
  summarizePetitions,
  filterPetitionRows,
  sortPetitionRows,
  buildRegionFilterOptions,
  PETITION_STATUS_FILTER_OPTIONS,
} from "./petitionWorkflowStats";

const DPAT_YEAR_START = 2020;

const DPAT_YEAR_OPTIONS = (() => {
  const endYear = new Date().getFullYear();
  const options = [];
  for (let year = endYear; year >= DPAT_YEAR_START; year -= 1) {
    options.push({ label: String(year), value: String(year) });
  }
  return options;
})();

const REVIEW_ROLES = new Set([
  "DPAT PETITION COMMITTEE",
  "Superuser",
  "DPAT TECHNICAL TEAM",
]);

/** Usernames allowed to review/accept/reject petitions at national level (in addition to REVIEW_ROLES). */
const REVIEW_USERNAMES = new Set(["fkwakye", "ofsam"]);

function DpatPetitionsPage() {
  const { orgTree, isOrgTreeLoading } = useLocationContext();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentYear = String(new Date().getFullYear());
  const yearFromQuery = searchParams.get("year");
  const initialYear =
    yearFromQuery && DPAT_YEAR_OPTIONS.some((o) => o.value === yearFromQuery)
      ? yearFromQuery
      : currentYear;

  const [reportingYear, setReportingYear] = useState(initialYear);
  const [petitionRows, setPetitionRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ percent: 0, loaded: 0, total: 0 });
  const [error, setError] = useState(null);
  const [regionFilter, setRegionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [reviewRow, setReviewRow] = useState(null);

  const username = String(user?.user?.username || "").trim().toLowerCase();

  const districtRows = useMemo(() => {
    const allRows = flattenDistrictsWithRegionsForPetitions(orgTree);
    
    // Filter for specific username 'ofsam' to show only AO Region and its districts
    if (username === "ofsam") {
      return allRows.filter((row) => {
        const regionName = String(row.regionName || "").trim();
        const districtName = String(row.districtName || "").trim();
        return (
          regionName === "AO Region" ||
          districtName === "AO District 1" ||
          districtName === "AO District 2"
        );
      });
    }
    
    return allRows;
  }, [orgTree, username]);

  const canReview = Boolean(
    user?.user?.userRoles?.some((role) => REVIEW_ROLES.has(role.name)) ||
      REVIEW_USERNAMES.has(username),
  );

  const summary = useMemo(() => summarizePetitions(petitionRows), [petitionRows]);

  useEffect(() => {
    setSearchParams({ year: reportingYear }, { replace: true });
  }, [reportingYear, setSearchParams]);

  useEffect(() => {
    if (!districtRows.length || isOrgTreeLoading) {
      setPetitionRows([]);
      setFetchProgress({ percent: 0, loaded: 0, total: 0 });
      return;
    }

    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      setPetitionRows([]);
      setFetchProgress({ percent: 0, loaded: 0, total: districtRows.length });
      try {
        const rows = await fetchDistrictPetitions(
          districtRows,
          reportingYear,
          controller.signal,
          (progress) => {
            if (!controller.signal.aborted) setFetchProgress(progress);
          },
        );
        if (!controller.signal.aborted) setPetitionRows(rows);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load petitions.");
        setPetitionRows([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [districtRows, reportingYear, isOrgTreeLoading]);

  useEffect(() => {
    setRegionFilter("all");
    setStatusFilter("all");
    setSearch("");
  }, [reportingYear]);

  const regionOptions = useMemo(() => buildRegionFilterOptions(petitionRows), [petitionRows]);

  const filteredRows = useMemo(
    () =>
      sortPetitionRows(
        filterPetitionRows(petitionRows, { regionKey: regionFilter, statusFilter, search }),
      ),
    [petitionRows, regionFilter, statusFilter, search],
  );

  const headlineMetrics = [
    {
      title: "Petitions submitted",
      value: String(summary.submittedCount),
      unit: `/ ${summary.eligibleCount} eligible`,
      icon: "FileText",
    },
    {
      title: "Pending review",
      value: String(summary.pendingCount),
      unit: "awaiting decision",
      icon: "Clock",
    },
    {
      title: "Accepted",
      value: String(summary.acceptedCount),
      unit: "petitions",
      icon: "CheckCircle",
    },
    {
      title: "Rejected",
      value: String(summary.rejectedCount),
      unit: "petitions",
      icon: "XCircle",
    },
    {
      title: "Regions with petitions",
      value: String(summary.regionBreakdown.length),
      unit: "regions",
      icon: "MapPin",
    },
  ];

  const handleDecisionSaved = (updatedRow) => {
    setPetitionRows((prev) =>
      prev.map((row) => (row.districtId === updatedRow.districtId ? updatedRow : row)),
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <ExportToolbar />
      <main className="main-content">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <Link
              to={`/ghana/dpat-dashboard?year=${reportingYear}`}
              className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-[#16325a] hover:underline"
            >
              <ArrowLeftOutlined />
              Back to DPAT Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-foreground md:text-3xl lg:text-4xl">
              District Petitions
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground md:text-base">
              National registry of district DPAT petitions. Review submissions by region, filter by
              decision status, and accept or reject petitions for the selected assessment year.
            </p>
          </div>
          <div className="w-full md:w-56">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Assessment year
            </label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#16325a]/25"
              value={reportingYear}
              onChange={(e) => setReportingYear(e.target.value)}
            >
              {DPAT_YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading && districtRows.length > 0 && (
          <div className="mb-6 rounded-xl border border-border bg-card px-4 py-4 shadow-sm md:px-5">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">
                Loading petitions for {reportingYear}… {fetchProgress.percent}%
              </span>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#16325a] border-t-transparent" />
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-[#16325a] transition-all"
                style={{ width: `${fetchProgress.percent}%` }}
              />
            </div>
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {loading && districtRows.length > 0
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-36 animate-pulse rounded-xl border border-border bg-card" />
              ))
            : headlineMetrics.map((m, i) => (
                <DpatMetricCard key={m.title} {...m} styleIndex={i} />
              ))}
        </div>

        {summary.regionBreakdown.length > 0 && (
          <section className="mb-6 rounded-xl border border-border bg-card p-4 shadow-sm md:p-6">
            <h3 className="mb-4 text-base font-semibold text-foreground">Petitions by region</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {summary.regionBreakdown.slice(0, 8).map((region) => (
                <button
                  key={region.regionKey}
                  type="button"
                  onClick={() => setRegionFilter(region.regionKey)}
                  className="rounded-lg border border-border bg-background px-4 py-3 text-left transition-colors hover:border-[#16325a]/30 hover:bg-[#16325a]/5"
                >
                  <p className="font-medium text-foreground">{region.regionName}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {region.count} submitted · {region.pending} pending
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="mb-4 flex flex-wrap gap-2">
          {[
            { value: "all", label: "All" },
            { value: "pending", label: "Pending" },
            { value: "accepted", label: "Accepted" },
            { value: "rejected", label: "Rejected" },
          ].map((chip) => (
            <Button
              key={chip.value}
              type={statusFilter === chip.value ? "primary" : "default"}
              onClick={() => setStatusFilter(chip.value)}
              style={
                statusFilter === chip.value
                  ? { backgroundColor: "#16325a", borderColor: "#16325a" }
                  : undefined
              }
            >
              {chip.label}
            </Button>
          ))}
        </div>

        <PetitionTable
          rows={filteredRows}
          loading={loading && districtRows.length > 0}
          reportingYear={reportingYear}
          regionFilter={regionFilter}
          onRegionFilterChange={setRegionFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          search={search}
          onSearchChange={setSearch}
          regionOptions={regionOptions}
          statusOptions={PETITION_STATUS_FILTER_OPTIONS}
          canReview={canReview}
          onReviewRow={setReviewRow}
        />

        <PetitionReviewModal
          open={Boolean(reviewRow)}
          row={reviewRow}
          reportingYear={reportingYear}
          canReview={canReview}
          user={user}
          onClose={() => setReviewRow(null)}
          onDecisionSaved={handleDecisionSaved}
        />
      </main>
    </div>
  );
}

export default DpatPetitionsPage;
